/**
 * Frontend script for the GoDAM Document block.
 *
 * Mounts the chrome-less pdf.js viewer over each rendered document. render.php emits only
 * the container plus a download link, so with JavaScript off — or if pdf.js fails to load —
 * the visitor still gets a usable link rather than an empty box.
 */

/**
 * WordPress dependencies
 */
import { createRoot, createElement } from '@wordpress/element';

/**
 * Render the PDF with the browser's built-in viewer.
 *
 * Only used when pdf.js could not be loaded at all — a chunk that 404s after a partial
 * deploy, or a browser that refuses the worker. The native viewer shows its own toolbar
 * (which is the whole reason we don't use it by default), but a preview with a toolbar beats
 * no preview.
 *
 * @param {HTMLElement} wrapper    The document container.
 * @param {string}      previewUrl URL of the PDF to display.
 */
function renderNativeFallback( wrapper, previewUrl ) {
	if ( ! previewUrl || ! isSafeUrl( previewUrl ) ) {
		return;
	}

	const object = document.createElement( 'object' );

	object.type = 'application/pdf';
	object.width = '100%';
	object.height = '100%';

	// Assigned as-is after protocol validation. encodeURI() would escape existing percent
	// signs a second time — turning a legitimate "%20" into "%2520" and breaking the URL.
	object.setAttribute( 'data', previewUrl );

	// Keep whatever fallback markup render.php emitted as the <object>'s own fallback, so a
	// browser with no PDF support still shows the download link.
	while ( wrapper.firstChild ) {
		object.appendChild( wrapper.firstChild );
	}

	wrapper.appendChild( object );
}

/**
 * Only http(s) URLs may be handed to the viewer or the fallback <object>.
 *
 * Blocks javascript:, data: and friends. The extension is deliberately NOT checked: a
 * preview URL is generated by GoDAM Central and need not end in `.pdf`.
 *
 * @param {string} url The URL to validate.
 * @return {boolean} True when the URL is safe to load.
 */
function isSafeUrl( url ) {
	try {
		const { protocol } = new URL( String( url ).trim(), window.location.href );

		return 'http:' === protocol || 'https:' === protocol;
	} catch ( e ) {
		return false;
	}
}

/**
 * Load the viewer chunk and mount it into a container.
 *
 * @param {HTMLElement} wrapper The document container.
 */
async function mountViewer( wrapper ) {
	const previewUrl = wrapper.getAttribute( 'data-godam-preview' );
	const title = wrapper.getAttribute( 'data-godam-title' ) || '';

	if ( ! previewUrl || ! isSafeUrl( previewUrl ) ) {
		return;
	}

	// Claimed before the first await: mounting is triggered from more than one place now
	// (page load and, in Elementor, each widget render), and two passes reaching the same
	// wrapper would put a second React root over a live one.
	if ( wrapper.dataset.godamMounted ) {
		return;
	}

	wrapper.dataset.godamMounted = 'true';

	/*
	 * render.php's fallback paragraph, kept aside before React takes the container over.
	 *
	 * It carries the download link, and it is the only thing standing between a visitor and a
	 * dead end if the preview cannot be shown. Clearing the container to mount React destroys
	 * it, so the error path below has nothing to fall back to unless it is saved here first.
	 */
	const serverFallback = wrapper.firstElementChild
		? wrapper.firstElementChild.cloneNode( true )
		: null;

	let DocumentViewer;

	try {
		// Lazily imported so pdf.js — by far the heaviest thing this block loads — stays out
		// of the page until a document is actually about to be shown. Same pattern as the
		// player's plugin loader.
		( { default: DocumentViewer } = await import(
			/* webpackChunkName: "godam-pdf-viewer" */ './viewer'
		) );
	} catch ( error ) {
		renderNativeFallback( wrapper, previewUrl );
		return;
	}

	// React owns the container from here, so clear the server-rendered fallback first.
	wrapper.textContent = '';

	const root = createRoot( wrapper );

	/*
	 * One failure can be reported more than once: react-pdf routes onLoadError, onSourceError
	 * and onPassword to the viewer's error handler, which calls this back each time. Without
	 * the guard a second call unmounts an already-unmounted root and appends a second
	 * <object>, showing the visitor the document twice.
	 */
	let handledError = false;

	root.render(
		createElement( DocumentViewer, {
			url: previewUrl,
			title,
			onError: () => {
				// pdf.js loaded but could not render this file — most often a CORS failure on
				// the CDN, since pdf.js fetches over XHR where an <object> did not. Hand it to
				// the native viewer, which is not subject to CORS.
				if ( handledError ) {
					return;
				}

				handledError = true;

				root.unmount();
				wrapper.textContent = '';

				// Restore the server-rendered paragraph so renderNativeFallback() can hand it
				// to the <object> as its own fallback content, download link intact. A browser
				// that cannot display PDFs either then still offers the visitor the file,
				// rather than a sentence telling them it cannot be shown and nothing else.
				if ( serverFallback ) {
					wrapper.appendChild( serverFallback );
				}

				renderNativeFallback( wrapper, previewUrl );
			},
		} ),
	);
}

/**
 * Mount every document in a subtree, once it scrolls into view.
 *
 * @param {Element|Document} root Element (or document) to search for document wrappers.
 */
function mountViewersIn( root ) {
	const wrappers = Array.from(
		root.querySelectorAll( '.godam-pdf-wrapper[data-godam-preview]' ),
	).filter( ( wrapper ) => ! wrapper.dataset.godamMounted );

	if ( ! wrappers.length ) {
		return;
	}

	// Nothing is fetched until the document scrolls into view: a block far down a long page
	// should not cost every visitor a pdf.js download plus a multi-megabyte PDF.
	if ( ! ( 'IntersectionObserver' in window ) ) {
		wrappers.forEach( mountViewer );
		return;
	}

	const observer = new IntersectionObserver(
		( entries ) => {
			entries.forEach( ( entry ) => {
				if ( ! entry.isIntersecting ) {
					return;
				}

				observer.unobserve( entry.target );
				mountViewer( entry.target );
			} );
		},
		// Start loading just before the block reaches the viewport, so scrolling to it
		// usually finds it already rendered.
		{ rootMargin: '200px' },
	);

	wrappers.forEach( ( wrapper ) => observer.observe( wrapper ) );
}

/*
 * Waiting for DOMContentLoaded is not enough on its own: this script can be enqueued late
 * enough that the event has already fired (Elementor's editor preview loads widget assets
 * dynamically), and then nothing would ever mount.
 */
if ( 'loading' === document.readyState ) {
	document.addEventListener( 'DOMContentLoaded', () => mountViewersIn( document ) );
} else {
	mountViewersIn( document );
}

/*
 * Elementor renders a widget's markup over AJAX every time it is added or edited, long after
 * DOMContentLoaded, so a document dropped into the editor kept showing render.php's
 * no-JavaScript fallback ("This document cannot be displayed here") while the published page
 * was fine. `frontend/element_ready` is Elementor's own signal that a widget's DOM is in
 * place; it also fires for the initial render, and mountViewer() ignores wrappers it has
 * already claimed, so the two entry points cannot collide.
 *
 * Registered defensively: this same script runs on plain WordPress pages, where none of these
 * globals exist and the event never fires.
 */
window.addEventListener( 'elementor/frontend/init', () => {
	const hooks = window.elementorFrontend?.hooks;

	if ( ! hooks ) {
		return;
	}

	hooks.addAction(
		'frontend/element_ready/godam-document.default',
		// Elementor hands the widget over as a jQuery object.
		( element ) => mountViewersIn( element?.[ 0 ] || document ),
	);
} );

/**
 * Detect a page-builder editor preview (WPBakery's inline editor, or Elementor's).
 *
 * Both re-render element markup into the preview after this script has run, and again on
 * every edit. WPBakery offers no per-element "rendered" signal to hook the way Elementor's
 * `element_ready` does, so the preview is watched instead — see below.
 *
 * Same detection as godam-image-layers/frontend.js and godam-player/frontend.js, which solve
 * this for the hotspot layers and the player.
 *
 * @return {boolean} True when running inside a builder's editor preview.
 */
function isBuilderPreview() {
	try {
		const frame = window.frameElement;

		// WPBakery inline editor iframe ids/classes, and Elementor's preview iframe id.
		if (
			frame &&
			/vc[-_]inline-frame|vc_editor|elementor-preview-iframe/i.test(
				`${ frame.id || '' } ${ frame.className || '' }`,
			)
		) {
			return true;
		}
	} catch ( e ) {
		// Cross-origin frameElement access: not a builder preview we can serve anyway.
	}

	try {
		const classes = document.body?.classList;

		return !! (
			classes &&
			( classes.contains( 'vc_editor' ) ||
				classes.contains( 'elementor-editor-active' ) )
		);
	} catch ( e ) {
		return false;
	}
}

/*
 * Editor preview only: watch for element markup arriving.
 *
 * WPBakery's inline editor re-renders a shortcode over AJAX on every edit, exactly as
 * Elementor does, but publishes no equivalent of `element_ready` — so newly rendered
 * documents sat on the "cannot be displayed here" fallback until the page was reloaded. The
 * deferred passes cover markup that lands while the editor is still starting up; the observer
 * covers every edit after that. mountViewer() claims each wrapper, so repeated passes are
 * free.
 *
 * Nothing here is scheduled on a published page.
 */
if ( isBuilderPreview() ) {
	[ 300, 1200, 3000 ].forEach( ( delay ) =>
		setTimeout( () => mountViewersIn( document ), delay ),
	);

	if ( 'MutationObserver' in window ) {
		let scheduled = false;

		const observer = new MutationObserver( () => {
			// Coalesced into one pass: a single edit replaces a whole subtree, which would
			// otherwise mean a scan per added node.
			//
			// A timer rather than requestAnimationFrame, which does not run while a document is
			// hidden — and a builder hides its preview frame often enough (panel transitions, a
			// backgrounded editor tab) that the pass could be deferred indefinitely, leaving the
			// author looking at the fallback text when the frame came back.
			if ( scheduled ) {
				return;
			}

			scheduled = true;

			setTimeout( () => {
				scheduled = false;
				mountViewersIn( document );
			}, 50 );
		} );

		const startObserving = () =>
			observer.observe( document.body, { childList: true, subtree: true } );

		if ( document.body ) {
			startObserving();
		} else {
			document.addEventListener( 'DOMContentLoaded', startObserving );
		}
	}
}
